/**
 * BreakdownIcon — resolves a row's leading glyph from the card kind
 * + dimension + bucket label. One place for the mapping so each card
 * stays small and the icon vocabulary stays consistent across the
 * Geography / Devices / Sources / Pages cards.
 *
 *   Geography (country_code, region_code)  → SVG flag (`Flag`)
 *   Devices · form_factor                  → Phosphor device glyph
 *   Devices · os_family                    → Phosphor OS logo
 *   Devices · browser_family               → circle-with-letter
 *   Sources · referrer_host / utm_source   → `SourceIcon`
 *   Pages                                  → Phosphor file/page glyph
 */
import { Flag } from '@/components/primitives/Flag';
import { SourceIcon } from '@/components/primitives/SourceIcon';
import { cn } from '@/lib/utils';
import {
  AndroidLogo,
  AppleLogo,
  Desktop,
  DeviceMobile,
  DeviceTablet,
  File as FileIcon,
  Globe,
  LinuxLogo,
  WindowsLogo,
} from '@phosphor-icons/react';
import type { ReactElement } from 'react';

const ICON_CLASS = 'size-3.5';

function FormFactorIcon({ value }: { value: string }): ReactElement {
  switch (value.toLowerCase()) {
    case 'phone':
      return <DeviceMobile aria-hidden="true" className={ICON_CLASS} />;
    case 'tablet':
      return <DeviceTablet aria-hidden="true" className={ICON_CLASS} />;
    case 'desktop':
      return <Desktop aria-hidden="true" className={ICON_CLASS} />;
    default:
      return <Globe aria-hidden="true" className={ICON_CLASS} />;
  }
}

function OsIcon({ value }: { value: string }): ReactElement {
  const v = value.toLowerCase();
  if (v === 'macos' || v === 'ios') return <AppleLogo aria-hidden="true" className={ICON_CLASS} />;
  if (v === 'windows') return <WindowsLogo aria-hidden="true" className={ICON_CLASS} />;
  if (v === 'android') return <AndroidLogo aria-hidden="true" className={ICON_CLASS} />;
  if (v === 'linux') return <LinuxLogo aria-hidden="true" className={ICON_CLASS} />;
  return <Globe aria-hidden="true" className={ICON_CLASS} />;
}

const BROWSER_LETTER_BG: Record<string, string> = {
  chrome: 'bg-[#4285F4]/20 text-[#8AB4F8]',
  safari: 'bg-[#1E90FF]/20 text-[#73B7F3]',
  firefox: 'bg-[#FF7139]/20 text-[#FFA86A]',
  edge: 'bg-[#0078D4]/20 text-[#5CB7FF]',
  opera: 'bg-[#FF1B2D]/20 text-[#FF7283]',
  brave: 'bg-[#FB542B]/20 text-[#FF9A7A]',
  samsung: 'bg-[#1428A0]/20 text-[#85A7FF]',
};

/**
 * Tiny coloured circle with the browser's first letter. Avoids
 * pulling in a brand-icon library for a slot that's only ~14 px
 * wide; the letter + brand-ish tint is enough signal to telegraph
 * "Chrome" / "Safari" / etc. at this density.
 */
function BrowserBadge({ value }: { value: string }): ReactElement {
  const v = value.toLowerCase();
  const letter = v.charAt(0).toUpperCase() || '?';
  const tone = BROWSER_LETTER_BG[v] ?? 'bg-[var(--color-line)] text-[var(--color-ink-soft)]';
  return (
    <span
      aria-hidden="true"
      className={cn(
        'inline-flex size-3.5 items-center justify-center rounded-full text-[8px] font-bold leading-none',
        tone,
      )}
    >
      {letter}
    </span>
  );
}

export function BreakdownIcon({
  cardKey,
  dimension,
  bucket,
}: {
  cardKey: 'sources' | 'pages' | 'geo' | 'devices';
  /** Active breakdown dimension when known (one of the
   *  `pixel_breakdown` enum values). Pages tab doesn't have one. */
  dimension?: string;
  /** The row's bucket label — the country code, browser name, etc. */
  bucket: string;
}): ReactElement {
  if (cardKey === 'geo') {
    return <Flag code={bucket} className="h-3 w-[18px]" />;
  }
  if (cardKey === 'sources') {
    return <SourceIcon source={bucket} className={ICON_CLASS} />;
  }
  if (cardKey === 'devices') {
    if (dimension === 'os_family') return <OsIcon value={bucket} />;
    if (dimension === 'browser_family') return <BrowserBadge value={bucket} />;
    return <FormFactorIcon value={bucket} />;
  }
  // Pages card — bucket is a URL. A page glyph keeps the alignment
  // consistent with the icon-bearing cards without claiming meaning.
  return <FileIcon aria-hidden="true" className={ICON_CLASS} />;
}
